import PageHeader from '../../components/PageHeader';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Linkedin, Plug, KeyRound, PenSquare, ListChecks, Megaphone, Trash2, RefreshCw, ExternalLink, Repeat } from 'lucide-react';
import api from '../../services/api';
import { cn } from '../../utils/cn';
import RecurrencePicker from '../../components/social/RecurrencePicker';
import alertService from '../../utils/alert';

// LinkedIn integration page — self-contained, calls /api/linkedin/* through the
// shared axios instance. Ported UX from the working POC (Settings + Compose +
// Posts + Campaigns), adapted to the CRM's per-user credential model. The backend
// wraps every response as { success, data }, so we unwrap res.data.data here.
const unwrap = (res) => res?.data?.data ?? {};
const errMsg = (e) => e?.response?.data?.error || e?.response?.data?.message || e.message || 'Something went wrong';

// Shared field styling — mirrors the SLDS input treatment used across the CRM's CRM/Leads/Campaigns pages.
const FIELD_BASE = 'w-full px-4 bg-[#F3F2F2] border border-[#E5E5E5] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all';
const inputClass = `${FIELD_BASE} h-10`;
const textareaClass = `${FIELD_BASE} min-h-[120px] py-2.5`;
const btnPrimaryClass = 'h-10 px-4 bg-[#0176D3] text-white rounded text-[13px] font-bold hover:bg-[#014486] transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)] disabled:opacity-50 disabled:cursor-not-allowed';

const STATUS_BADGE = {
  active: 'sf-badge-success',
  revoked: 'sf-badge-neutral',
  expired: 'sf-badge-error',
  pending: 'sf-badge-warning',
  publishing: 'sf-badge-manager',
  published: 'sf-badge-success',
  failed: 'sf-badge-error',
  cancelled: 'sf-badge-neutral',
  creating: 'sf-badge-manager',
  paused: 'sf-badge-warning',
  archived: 'sf-badge-neutral',
  draft: 'sf-badge-neutral',
};

function Badge({ status }) {
  return (
    <span className={cn('sf-badge', STATUS_BADGE[status] || 'sf-badge-neutral')}>
      {status}
    </span>
  );
}

function Banner({ msg }) {
  if (!msg) return null;
  return (
    <div className={cn(
      'rounded px-4 py-3 text-[13px] font-medium border',
      msg.type === 'success' ? 'bg-[#EBF3ED] text-[#2E844A] border-[#C4DCC8]' : 'bg-[#FDE7E9] text-[#BA0517] border-[#F6A3AB]'
    )}>
      {msg.text}
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc }) {
  return (
    <div className="sf-card text-center py-16 px-4 space-y-3">
      <div className="w-14 h-14 bg-[#EAF4FE] rounded-full flex items-center justify-center mx-auto">
        <Icon size={26} className="text-[#0176D3]" />
      </div>
      <p className="font-bold text-[#2E2E2E] text-[13px]">{title}</p>
      {desc && <p className="text-[12px] text-[#5C5C5C] max-w-sm mx-auto">{desc}</p>}
    </div>
  );
}

const TABS = [
  { key: 'credentials', label: 'Setup', icon: KeyRound },
  { key: 'account', label: 'Account', icon: Plug },
  { key: 'compose', label: 'Compose', icon: PenSquare },
  { key: 'posts', label: 'Posts', icon: ListChecks },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
];

export default function LinkedInPage() {
  const [tab, setTab] = useState('account');
  const [searchParams, setSearchParams] = useSearchParams();
  const [banner, setBanner] = useState(null);

  // Show the connect result banner from the OAuth round-trip, then strip the params.
  useEffect(() => {
    if (searchParams.get('linkedin_connected')) {
      setBanner({ type: 'success', text: 'LinkedIn account connected.' });
      setTab('account');
    } else if (searchParams.get('linkedin_error')) {
      setBanner({ type: 'error', text: `LinkedIn connection failed: ${searchParams.get('linkedin_error')}` });
      setTab('account');
    }
    if (searchParams.get('linkedin_connected') || searchParams.get('linkedin_error')) {
      const next = new URLSearchParams(searchParams);
      next.delete('linkedin_connected');
      next.delete('linkedin_error');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Header */}
      <PageHeader title="LinkedIn" subtitle="Connect your LinkedIn account, publish and schedule posts, manage campaigns." icon={Linkedin} />

      <Banner msg={banner} />

      {/* Tabs */}
      <div className="flex items-center border-b border-[#E5E5E5] gap-6 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex items-center gap-2 pb-3 text-[13px] font-bold border-b-2 transition-all whitespace-nowrap shrink-0',
              tab === t.key ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#5C5C5C] hover:text-[#2E2E2E]'
            )}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'credentials' && <CredentialsTab />}
      {tab === 'account' && <AccountTab />}
      {tab === 'compose' && <ComposeTab />}
      {tab === 'posts' && <PostsTab />}
      {tab === 'campaigns' && <CampaignsTab />}
    </div>
  );
}

// ── Setup ───────────────────────────────────────────────────────────────────
// LinkedIn is run through the platform's own LinkedIn app: nothing to create or paste. This tab only says
// whether it is ready, and whether an older connection still uses an app the user brought earlier.
function CredentialsTab() {
  const [info, setInfo] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setInfo(unwrap(await api.get('/linkedin/credentials')));
      } catch (e) {
        setMsg({ type: 'error', text: errMsg(e) });
      }
    })();
  }, []);

  return (
    <div className="space-y-5">
      <Banner msg={msg} />
      <div className="sf-card p-6 max-w-2xl space-y-3 text-[13px]">
        <p className="font-bold text-[#014486]">LinkedIn is provided by the platform</p>
        <p className="text-[#2E2E2E]">You do not need to create a LinkedIn app or enter any keys. Open the <b>Account</b> tab and press <b>Connect LinkedIn</b>.</p>
        {info && !info.platformConfigured && (
          <p className="text-[#B35F00] bg-[#FFF4E5] border border-[#FFCD94] rounded px-3 py-2">LinkedIn has not been set up on this platform yet. Please contact support.</p>
        )}
        {info?.platformConfigured && <p className="text-[#2E844A]">Ready to connect.</p>}
        {info?.legacyApp && (
          <p className="text-[#706E6B]">Your existing connection still uses the LinkedIn app you added earlier ({info.legacyApp.clientId}). Disconnect and reconnect on the Account tab to move it to the platform app.</p>
        )}
      </div>
    </div>
  );
}

// ── Account (connect / disconnect) ───────────────────────────────────────────
function AccountTab() {
  const [accounts, setAccounts] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      // Disconnected accounts are kept (revoked, not deleted) so past posts and
      // schedules still resolve to them - but a revoked card has no further
      // action here, so hide it once disconnected rather than leaving a dead
      // "active"-looking row the disconnect button appears to do nothing to.
      const all = unwrap(await api.get('/linkedin/accounts')).accounts || [];
      setAccounts(all.filter((a) => a.status !== 'revoked'));
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setConnecting(true);
    setMsg(null);
    try {
      const { url } = unwrap(await api.get('/linkedin/connect'));
      window.location.href = url; // top-level navigation to LinkedIn's OAuth screen
    } catch (e) {
      setConnecting(false);
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };

  const disconnect = async (id) => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect this LinkedIn account? Scheduled posts using it will fail until reconnected.', 'danger', 'Disconnect'))) return;
    try {
      await api.delete(`/linkedin/accounts/${id}`);
      load();
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };

  return (
    <div className="space-y-5">
      <Banner msg={msg} />
      <button onClick={connect} disabled={connecting} className={cn(btnPrimaryClass, 'flex items-center gap-2')}>
        <Plug size={15} /> {connecting ? 'Redirecting…' : 'Connect LinkedIn'}
      </button>

      {accounts.length === 0 ? (
        <EmptyState icon={Linkedin} title="No LinkedIn accounts connected" desc="Press Connect LinkedIn to link your account." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((acc) => {
            const hasOrgScope = (acc.grantedScopes || []).includes('r_organization_admin');
            const orgCount = (acc.organizations || []).length;
            return (
              <div key={acc._id} className="sf-card p-4 space-y-2 hover:border-[#0176D3] transition-all">
                <div className="flex items-center gap-3">
                  {acc.picture ? <img src={acc.picture} alt="" className="w-10 h-10 rounded-full shrink-0" /> : <div className="w-10 h-10 rounded-full bg-[#F3F2F2] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#2E2E2E] text-[13px] truncate">{acc.name}</div>
                    <div className="text-[12px] text-[#5C5C5C] truncate">{acc.email}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge status={acc.status} />
                    <button onClick={() => disconnect(acc._id)} className="h-7 w-7 flex items-center justify-center rounded text-[#5C5C5C] hover:text-[#BA0517] hover:bg-[#FDE7E9] transition-colors" title="Disconnect" aria-label="Disconnect account">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {/* Diagnoses why Company Pages may be missing from the "Post as" dropdown - LinkedIn grants
                    r_organization_admin only for apps approved for the Community Management API product, so
                    a missing grant here always traces back to LinkedIn's own app approval, not this code. */}
                <div className="text-[11px] border-t border-[#F3F2F2] pt-2">
                  {!hasOrgScope ? (
                    <span className="text-[#BA0517]">Company Pages scope not granted by LinkedIn — your LinkedIn app likely isn&apos;t approved for the Community Management API product yet.</span>
                  ) : orgCount > 0 ? (
                    <span className="text-[#2E844A] font-semibold">{orgCount} Company Page{orgCount > 1 ? 's' : ''} available in &quot;Post as&quot;.</span>
                  ) : (
                    <span className="text-[#B35F00]">Organization scope granted, but no Company Pages found — confirm you&apos;re an ADMINISTRATOR of a page on LinkedIn.</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Compose ──────────────────────────────────────────────────────────────────
const MAX_IMAGES = 20;
function ComposeTab() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [authorUrn, setAuthorUrn] = useState('');
  const [text, setText] = useState('');
  const [media, setMedia] = useState([]);
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduledTime, setScheduledTime] = useState('');
  const [recurrence, setRecurrence] = useState({ frequency: 'none' });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef();
  const pollers = useRef({});

  useEffect(() => {
    api.get('/linkedin/accounts').then((r) => {
      const active = (unwrap(r).accounts || []).filter((a) => a.status === 'active');
      setAccounts(active);
      if (active[0]) setAccountId(active[0]._id);
    }).catch(() => {});
    return () => Object.values(pollers.current).forEach(clearInterval);
  }, []);

  const hasVideo = media.some((m) => m.type === 'video');
  const imageCount = media.filter((m) => m.type === 'image').length;

  const pollStatus = (id) => {
    pollers.current[id] = setInterval(async () => {
      try {
        const asset = unwrap(await api.get(`/linkedin/media/${id}/status`)).mediaAsset;
        setMedia((cur) => cur.map((m) => (m._id === id ? asset : m)));
        if (asset.uploadStatus === 'available' || asset.uploadStatus === 'failed') {
          clearInterval(pollers.current[id]);
        }
      } catch {
        clearInterval(pollers.current[id]);
      }
    }, 3000);
  };

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !accountId) return;
    const type = file.type.startsWith('video') ? 'video' : 'image';
    if (type === 'video' && media.length > 0) { setMsg({ type: 'error', text: 'A video must be posted alone.' }); return; }
    if (type === 'image' && (hasVideo || imageCount >= MAX_IMAGES)) { setMsg({ type: 'error', text: `Up to ${MAX_IMAGES} images, and never mixed with a video.` }); return; }
    setMsg(null);
    const form = new FormData();
    form.append('file', file);
    form.append('accountId', accountId);
    if (authorUrn) form.append('authorUrn', authorUrn);
    form.append('type', type);
    try {
      const asset = unwrap(await api.post('/linkedin/media/upload', form)).mediaAsset;
      setMedia((cur) => [...cur, asset]);
      if (asset.uploadStatus === 'processing') pollStatus(asset._id);
    } catch (e2) {
      setMsg({ type: 'error', text: errMsg(e2) });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeMedia = (id) => {
    clearInterval(pollers.current[id]);
    setMedia((cur) => cur.filter((m) => m._id !== id));
  };

  const blocked = media.some((m) => m.uploadStatus === 'uploading' || m.uploadStatus === 'processing');
  const anyFailed = media.some((m) => m.uploadStatus === 'failed');

  const submit = async (e) => {
    e.preventDefault();
    if (anyFailed) { setMsg({ type: 'error', text: 'Remove failed media before posting.' }); return; }
    if (scheduleMode === 'later' && recurrence.frequency === 'weekly' && !recurrence.daysOfWeek?.length) {
      setMsg({ type: 'error', text: 'Pick at least one day of the week to repeat on.' });
      return;
    }
    if (scheduleMode === 'later' && recurrence.frequency !== 'none' && recurrence.endType === 'on' && !recurrence.endDate) {
      setMsg({ type: 'error', text: 'Pick an end date for the recurrence.' });
      return;
    }
    setSubmitting(true);
    setMsg(null);
    try {
      await api.post('/linkedin/posts', {
        accountId,
        authorUrn: authorUrn || undefined,
        contentText: text,
        mediaAssetIds: media.map((m) => m._id),
        scheduledTime: scheduleMode === 'later' ? new Date(scheduledTime).toISOString() : undefined,
        recurrence: scheduleMode === 'later' && recurrence.frequency !== 'none'
          ? { ...recurrence, endDate: recurrence.endType === 'on' ? new Date(recurrence.endDate).toISOString() : undefined }
          : undefined,
      });
      setText('');
      setMedia([]);
      setRecurrence({ frequency: 'none' });
      setMsg({ type: 'success', text: scheduleMode === 'later' ? 'Post scheduled.' : 'Post submitted.' });
    } catch (e2) {
      setMsg({ type: 'error', text: errMsg(e2) });
    } finally {
      setSubmitting(false);
    }
  };

  if (accounts.length === 0) {
    return <EmptyState icon={PenSquare} title="No active LinkedIn account" desc="Connect an active LinkedIn account first (Account tab)." />;
  }

  // One entry per personal profile, plus one per Company Page it administers -
  // whichever is selected determines the author LinkedIn publishes the post as.
  const postAsOptions = accounts.flatMap((a) => [
    { value: `${a._id}|`, accountId: a._id, authorUrn: '', label: `${a.name} (${a.email})` },
    ...(a.organizations || []).map((o) => ({
      value: `${a._id}|${o.urn}`,
      accountId: a._id,
      authorUrn: o.urn,
      label: `${o.name} · Company Page`,
    })),
  ]);
  const postAsValue = `${accountId}|${authorUrn}`;
  const onPostAsChange = (value) => {
    const opt = postAsOptions.find((o) => o.value === value);
    if (!opt) return;
    setAccountId(opt.accountId);
    setAuthorUrn(opt.authorUrn);
  };

  return (
    <form onSubmit={submit} className="sf-card p-6 space-y-4 max-w-2xl">
      <Banner msg={msg} />
      <Field label="Post as">
        <select className={inputClass} value={postAsValue} onChange={(e) => onPostAsChange(e.target.value)}>
          {postAsOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Text">
        <textarea className={textareaClass} value={text} onChange={(e) => setText(e.target.value)} placeholder="What do you want to share?" />
      </Field>

      <div className="space-y-2">
        <label className="block text-[11px] font-semibold text-[#706E6B]">Media</label>
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onFile} className="text-[13px] text-[#5C5C5C]" />
        {media.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {media.map((m) => (
              <div key={m._id} className="flex items-center gap-2 text-[12px] border border-[#E5E5E5] rounded px-3 py-2 bg-[#F4F6F9]">
                <span className="flex-1 truncate font-medium text-[#2E2E2E]">{m.type} · {m.mimeType}</span>
                <Badge status={m.uploadStatus} />
                <button type="button" onClick={() => removeMedia(m._id)} className="text-[#5C5C5C] hover:text-[#BA0517]"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setScheduleMode('now')} className={cn('px-3 py-1.5 rounded text-[12px] font-bold border transition-all', scheduleMode === 'now' ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#E5E5E5] text-[#2E2E2E] hover:bg-[#F3F2F2]')}>Post now</button>
        <button type="button" onClick={() => setScheduleMode('later')} className={cn('px-3 py-1.5 rounded text-[12px] font-bold border transition-all', scheduleMode === 'later' ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#E5E5E5] text-[#2E2E2E] hover:bg-[#F3F2F2]')}>Schedule for later</button>
      </div>
      {scheduleMode === 'later' && (
        <>
          <Field label="When">
            <input className={inputClass} type="datetime-local" value={scheduledTime} min={new Date().toISOString().slice(0, 16)} onChange={(e) => setScheduledTime(e.target.value)} required />
          </Field>
          <RecurrencePicker onChange={setRecurrence} />
        </>
      )}

      <button disabled={submitting || blocked || (!text && media.length === 0)} className={btnPrimaryClass}>
        {submitting ? 'Submitting…' : blocked ? 'Waiting for media…' : scheduleMode === 'later' ? 'Schedule post' : 'Post now'}
      </button>
    </form>
  );
}

// ── Posts ────────────────────────────────────────────────────────────────────
function PostsTab() {
  const [posts, setPosts] = useState([]);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try { setPosts(unwrap(await api.get('/linkedin/posts')).posts || []); } catch (e) { setMsg({ type: 'error', text: errMsg(e) }); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // background cron changes statuses
    return () => clearInterval(t);
  }, []);

  const cancel = async (id) => {
    if (!(await alertService.confirm('Cancel scheduled post?', 'Cancel this scheduled post?', 'warning', 'Cancel post'))) return;
    try { await api.delete(`/linkedin/posts/${id}`); load(); } catch (e) { setMsg({ type: 'error', text: errMsg(e) }); }
  };

  return (
    <div className="space-y-5">
      <Banner msg={msg} />
      {posts.length === 0 ? (
        <EmptyState icon={ListChecks} title="No posts yet" desc="Posts you publish or schedule will show up here." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {posts.map((p) => (
            <div key={p._id} className="sf-card p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge status={p.status} />
                <span className="text-[11px] text-[#5C5C5C]">{new Date(p.publishedAt || p.scheduledTime).toLocaleString()}</span>
                <div className="flex-1" />
                {p.status === 'pending' && <button onClick={() => cancel(p._id)} className="text-[11px] font-bold text-[#BA0517] hover:underline">Cancel</button>}
                {p.status === 'published' && p.linkedinPostUrn && (
                  <a href={`https://www.linkedin.com/feed/update/${p.linkedinPostUrn}/`} target="_blank" rel="noreferrer" className="text-[11px] font-bold text-[#0176D3] hover:underline flex items-center gap-1">
                    View <ExternalLink size={12} />
                  </a>
                )}
              </div>
              {p.contentText && <p className="text-[13px] text-[#2E2E2E] whitespace-pre-wrap">{p.contentText}</p>}
              {p.mediaAssets?.length > 0 && <p className="text-[11px] text-[#5C5C5C] mt-1">{p.mediaAssets.length} media attachment(s)</p>}
              {p.recurrence?.frequency !== 'none' && (
                <p className="text-[11px] text-[#0176D3] mt-1 flex items-center gap-1">
                  <Repeat size={11} /> Repeats {p.recurrence.frequency} · #{p.occurrenceIndex}
                </p>
              )}
              {p.errorMessage && <p className="text-[11px] text-[#BA0517] mt-1 break-all">{p.errorMessage}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Campaigns (ads — scaffolded, unverified) ─────────────────────────────────
function CampaignsTab() {
  const [accounts, setAccounts] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [msg, setMsg] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const [a, ad, c] = await Promise.all([
        api.get('/linkedin/accounts'),
        api.get('/linkedin/ads/accounts'),
        api.get('/linkedin/ads/campaigns'),
      ]);
      setAccounts((unwrap(a).accounts || []).filter((x) => x.status === 'active'));
      setAdAccounts(unwrap(ad).adAccounts || []);
      setCampaigns(unwrap(c).campaigns || []);
    } catch (e) { setMsg({ type: 'error', text: errMsg(e) }); }
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    if (!accounts[0]) { setMsg({ type: 'error', text: 'Connect a LinkedIn account first.' }); return; }
    setSyncing(true); setMsg(null);
    try { await api.post('/linkedin/ads/accounts/sync', { accountId: accounts[0]._id }); await load(); }
    catch (e) { setMsg({ type: 'error', text: errMsg(e) }); }
    finally { setSyncing(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/linkedin/ads/campaigns/${id}/status`, { status }); load(); }
    catch (e) { setMsg({ type: 'error', text: errMsg(e) }); }
  };

  return (
    <div className="space-y-5">
      <div className="sf-card p-4 bg-[#FFF4E5] border-[#FFCD94] text-[12px] text-[#B35F00]">
        Ads require LinkedIn Marketing Developer Platform approval (<code>r_ads</code>/<code>rw_ads</code>). This module is
        scaffolded and will show a clear "reconnect to grant scope" message until your app is approved and the account is reconnected.
      </div>
      <Banner msg={msg} />

      <button onClick={sync} disabled={syncing} className={cn(btnPrimaryClass, 'flex items-center gap-2')}>
        <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync ad accounts'}
      </button>

      {adAccounts.length > 0 && (
        <div>
          <h3 className="text-[11px] font-semibold text-[#706E6B] mb-2">Ad accounts</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {adAccounts.map((a) => (
              <div key={a._id} className="sf-card px-4 py-3 text-[13px]">
                <span className="font-bold text-[#2E2E2E]">{a.name}</span>
                <span className="text-[#5C5C5C]"> · {a.currency} · {a.authorizedRole}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-[11px] font-semibold text-[#706E6B] mb-2">Campaigns</h3>
        {campaigns.length === 0 ? (
          <EmptyState icon={Megaphone} title="No campaigns yet" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {campaigns.map((c) => (
              <div key={c._id} className="sf-card p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#2E2E2E] text-[13px] truncate">{c.name}</div>
                  <div className="text-[11px] text-[#5C5C5C]">{c.objective} · {c.currency} {c.dailyBudget}/day</div>
                  {c.errorMessage && <div className="text-[11px] text-[#BA0517] break-all">{c.errorMessage}</div>}
                </div>
                <Badge status={c.status} />
                <div className="flex items-center gap-2 shrink-0">
                  {c.status === 'paused' && <button onClick={() => setStatus(c._id, 'active')} className="text-[11px] font-bold text-[#2E844A] hover:underline">Resume</button>}
                  {c.status === 'active' && <button onClick={() => setStatus(c._id, 'paused')} className="text-[11px] font-bold text-[#B35F00] hover:underline">Pause</button>}
                  {c.status !== 'archived' && <button onClick={() => setStatus(c._id, 'archived')} className="text-[11px] font-semibold text-[#706E6B] hover:underline">Archive</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Small shared bits ────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[11px] font-semibold text-[#706E6B]">{label}</span>
      {children}
    </label>
  );
}
